/**
 * Confirm and fix next-day ERP duplicates from the shortage report.
 *
 * The ERP repeated a schedule slot the next day with the same qty and booked the
 * receipt against the repeat, while Helix attributed it to the original. Fixing a
 * pair moves the receipts to the next-day slot, cancels the earlier slot and
 * accepts its ERP-only balance as an ERP duplicate. The dialog lists exactly the
 * pairs that will change — the ticked ones, or every pair for the filters.
 */
import { useEffect, useRef, useState } from 'react';
import {
  erpShortageAPI,
  type ErpNextDayDuplicateFixResult,
  type ErpNextDayDuplicatePair,
  type ErpShortageFilters,
} from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface FixNextDayDuplicatesDialogProps {
  open: boolean;
  customerId: number;
  filters: ErpShortageFilters;
  /** Ticked pairs by earlier slot id; empty means every pair for the filters. */
  selectedIds: Set<number>;
  /** Viewer's local date (yyyy-mm-dd). */
  today: string;
  onClose: () => void;
  /** Called after a fix request completes, whatever it changed. */
  onFixed: (result: ErpNextDayDuplicateFixResult) => void;
}

function detailOf(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

export default function FixNextDayDuplicatesDialog({
  open, customerId, filters, selectedIds, today, onClose, onFixed,
}: FixNextDayDuplicatesDialogProps) {
  const { formatDate, formatNumber } = useFormatters();
  const [pairs, setPairs] = useState<ErpNextDayDuplicatePair[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fixing, setFixing] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  // Read when the dialog opens; the page's filters cannot change while it is open.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPairs(null);
    setError(null);
    setFixError(null);
    setLoading(true);
    erpShortageAPI.nextDayDuplicates(customerId, filtersRef.current)
      .then((res) => { if (!cancelled) setPairs(res.pairs); })
      .catch((err: unknown) => {
        console.error('Next-day duplicates load failed:', err);
        if (!cancelled) setError(detailOf(err, 'Failed to load the next-day duplicates'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, customerId]);

  const bySelection = selectedIds.size > 0;
  const toFix = (pairs ?? []).filter((p) => !bySelection || selectedIds.has(p.earlier_schedule_id));
  const gone = bySelection && pairs ? selectedIds.size - toFix.length : 0;

  const handleFix = async () => {
    setFixing(true);
    setFixError(null);
    try {
      const result = await erpShortageAPI.fixNextDayDuplicates(customerId, toFix.map((p) => p.earlier_schedule_id), today);
      if (result.fixed.length > 0) {
        toast.success(`Fixed ${plural(result.fixed.length, 'next-day duplicate')}`);
      }
      if (result.skipped.length > 0) {
        const first = result.skipped[0];
        toast.warning(`${plural(result.skipped.length, 'pair')} left unchanged`, {
          description: `${first.label ?? `Schedule ${first.earlier_schedule_id}`}: ${first.reason}${result.skipped.length > 1 ? ' (and others)' : ''}`,
        });
      }
      onFixed(result);
    } catch (err: unknown) {
      console.error('Next-day duplicates fix failed:', err);
      const message = detailOf(err, 'Failed to fix the next-day duplicates');
      setFixError(message);
      toast.error(message);
    } finally {
      setFixing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !fixing) onClose(); }}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Fix next-day ERP duplicates</DialogTitle>
          <DialogDescription>
            The ERP repeated these slots the next day with the same qty and booked the receipt against the repeat.
            For each pair Helix moves the receipt to the next-day slot, cancels the earlier slot, and accepts the
            ERP balance left on it with the reason “ERP duplicate of next-day slot”.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-auto rounded-md border">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Finding next-day duplicates…
            </div>
          ) : error ? (
            <div className="py-10 text-center text-sm text-red-600">{error}</div>
          ) : toFix.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {bySelection
                ? 'None of the selected pairs still match — reload the report.'
                : 'No next-day ERP duplicates match the current filters.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left">
                  <th className="py-2 px-3 font-medium">PO / Line</th>
                  <th className="py-2 px-3 font-medium">Supplier</th>
                  <th className="py-2 px-3 font-medium text-right">Qty</th>
                  <th className="py-2 px-3 font-medium">Cancelled</th>
                  <th className="py-2 px-1" aria-hidden />
                  <th className="py-2 px-3 font-medium">Receives the receipt</th>
                  <th className="py-2 px-3 font-medium">Receipts moving</th>
                </tr>
              </thead>
              <tbody>
                {toFix.map((p) => (
                  <tr key={p.earlier_schedule_id} className="border-b last:border-0 align-top">
                    <td className="py-2 px-3 whitespace-nowrap">
                      <div className="font-medium">{p.po_number}/{p.line_number}</div>
                      <div className="text-xs text-muted-foreground">{p.part_number ?? '—'}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div>{p.supplier_name ?? '—'}</div>
                      {p.supplier_code && <div className="text-xs text-muted-foreground">{p.supplier_code}</div>}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{formatNumber(Number(p.qty))}</td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      <div>Sched {p.earlier.schedule_line_number ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(p.earlier.schedule_date)}</div>
                      {!p.earlier.sent_by_erp && (
                        <div className="text-[11px] text-amber-700">No longer sent by the ERP</div>
                      )}
                    </td>
                    <td className="py-2 px-1 text-muted-foreground"><ArrowRight className="h-4 w-4 mt-1" /></td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      <div>Sched {p.next_day.schedule_line_number ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(p.next_day.schedule_date)}</div>
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {p.receipts.map((g, i) => (
                        <div key={`${g.grn_number}-${i}`} className="whitespace-nowrap">
                          #{g.grn_number} · {formatDate(g.receipt_date)} · qty {formatNumber(Number(g.qty))}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {fixError && (
          <div role="alert" className="flex gap-2 items-start rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>The fix did not complete — {fixError.replace(/\.$/, '')}. Close the dialog and reload the report to see what changed before trying again.</span>
          </div>
        )}
        {gone > 0 && (
          <p className="text-xs text-amber-700">
            {plural(gone, 'selected pair')} no longer {gone === 1 ? 'matches' : 'match'} and will be left alone.
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={fixing}>Cancel</Button>
          <Button onClick={handleFix} disabled={fixing || loading || toFix.length === 0}>
            {fixing && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Fix {plural(toFix.length, 'pair')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
